import { useRef, useState } from "react";
import { Camera, LifeBuoy } from "lucide-react";
import { SignOutButton } from "@clerk/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Logo } from "@/components/AppLayout";
import { UserAvatar } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { SPECS } from "@/lib/labels";

const STEPS = [
  { t: "Show who you are", d: "Companies trust profiles with a real photo and location.", ok: (m) => m.avatar_path && (m.name || "").trim().length >= 2 && m.city && m.country },
  { t: "What do you do?", d: "We use this to match you with the right jobs.", ok: (m) => m.specializations?.length && m.experience_years != null },
  { t: "A few words about you", d: "Two or three sentences are enough.", ok: (m) => (m.bio || "").trim().length >= 20 },
];

const F = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

function StepBasics({ f, set, me, setMe }) {
  const api = useApi();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { setMe({ ...me, ...(await api("post", "/me/avatar", fd)) }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <UserAvatar user={me} size={80} testId="wizard-avatar" />
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files[0])} data-testid="wizard-avatar-input" />
        <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid="wizard-avatar-button">
          <Camera size={16} />{busy ? "Uploading..." : me.avatar_path ? "Change photo" : "Add photo"}
        </Button>
      </div>
      <F label="Full name"><Input value={f.name} onChange={set("name")} data-testid="wizard-name-input" /></F>
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="City"><Input value={f.city} onChange={set("city")} placeholder="e.g. Houston" data-testid="wizard-city-input" /></F>
        <F label="Country"><Input value={f.country} onChange={set("country")} placeholder="e.g. USA" data-testid="wizard-country-input" /></F>
      </div>
    </div>
  );
}

function StepTrade({ f, setF, set }) {
  const toggle = (s) => setF({ ...f, specializations: f.specializations.includes(s) ? f.specializations.filter((x) => x !== s) : [...f.specializations, s] });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-2">
        {Object.entries(SPECS).map(([k, v]) => (
          <label key={k} className={`flex items-center gap-2 p-3 rounded-md border-2 text-sm cursor-pointer ${f.specializations.includes(k) ? "border-sky-600 bg-sky-50" : "border-slate-200"}`}>
            <Checkbox checked={f.specializations.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`wizard-spec-${k}`} />{v}
          </label>
        ))}
      </div>
      <F label="Years of experience in wind"><Input type="number" min={0} max={60} value={f.experience_years} onChange={set("experience_years")} data-testid="wizard-years-input" className="max-w-[140px]" /></F>
    </div>
  );
}

const StepBio = ({ f, set }) => (
  <div className="space-y-5">
    <F label="Headline (optional)"><Input value={f.headline} onChange={set("headline")} placeholder="e.g. HV electrician, 7 years offshore" data-testid="wizard-headline-input" /></F>
    <F label="About me">
      <Textarea rows={5} value={f.bio} onChange={set("bio")} data-testid="wizard-bio-input"
        placeholder="Which turbines you know, onshore or offshore, what kind of work you are looking for..." />
      <p className={`text-xs ${f.bio.trim().length >= 20 ? "text-emerald-700" : "text-slate-500"}`} data-testid="wizard-bio-counter">{f.bio.trim().length} / 20 characters minimum</p>
    </F>
  </div>
);

export default function ProfileWizard() {
  const api = useApi();
  const { me, setMe } = useMe();
  const [step, setStep] = useState(() => Math.max(0, STEPS.findIndex((s) => !s.ok(me))));
  const [f, setF] = useState({ name: me.name || "", city: me.city || "", country: me.country || "", specializations: me.specializations || [],
    experience_years: me.experience_years ?? "", headline: me.headline || "", bio: me.bio || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const draft = { ...me, ...f, experience_years: f.experience_years === "" ? null : Number(f.experience_years) };
  const bodies = [{ name: f.name, city: f.city, country: f.country }, { specializations: f.specializations, experience_years: draft.experience_years }, { headline: f.headline, bio: f.bio }];

  const next = async () => {
    setBusy(true);
    try {
      const updated = await api("put", "/me/profile", bodies[step]);
      if (step < STEPS.length - 1) { setMe(updated); setStep(step + 1); }
      else { setMe(updated); toast.success("Profile ready. Welcome aboard!"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const S = STEPS[step];
  const Body = [StepBasics, StepTrade, StepBio][step];
  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-10">
      <Logo />
      <div className="rise mt-8 w-full max-w-xl panel p-6 sm:p-8" data-testid="profile-wizard">
        <div className="flex items-center justify-between"><p className="eyebrow" data-testid="wizard-step-label">Step {step + 1} / {STEPS.length}</p>
          <span className="text-xs text-slate-500">Required to use WindWorkHub</span></div>
        <Progress value={((step + 1) / STEPS.length) * 100} className="mt-3 h-1.5" />
        <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold tracking-tight">{S.t}</h1>
        <p className="mt-1 text-slate-600 text-sm">{S.d}</p>
        <div className="mt-6"><Body f={f} setF={setF} set={set} me={me} setMe={setMe} /></div>
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button variant="ghost" disabled={step === 0 || busy} onClick={() => setStep(step - 1)} data-testid="wizard-back-button">Back</Button>
          <Button disabled={busy || !S.ok(draft)} onClick={next} className="h-11 px-8" data-testid="wizard-next-button">
            {busy ? "Saving..." : step === STEPS.length - 1 ? "Finish" : "Continue"}
          </Button>
        </div>
      </div>
      <div className="mt-6 flex gap-5 text-sm text-slate-500">
        <a href="mailto:info@wg-exakt.se" className="flex items-center gap-1 hover:text-slate-900" data-testid="wizard-help-link"><LifeBuoy size={14} />Need help?</a>
        <SignOutButton><button className="hover:text-slate-900" data-testid="wizard-signout-button">Sign out</button></SignOutButton>
      </div>
    </div>
  );
}
